import type {JSX, ReactNode} from 'react';

import {capitalizeWords} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

import type {AfriexTestTransaction} from './types';

const toText = (value: unknown): string =>
  typeof value === 'string' ? value : JSON.stringify(value, null, 2);

const Json = ({value}: {value: unknown}) => (
  <pre className="whitespace-pre-wrap break-words text-xs">{toText(value)}</pre>
);

export default function TestTransactionSummary({
  data,
}: {
  data: AfriexTestTransaction;
}): JSX.Element {
  const fieldsToDisplay: [string, ReactNode][] = [
    ['Type', capitalizeWords(data.type?.replace(/_/g, ' '))],
    ['Processor Transaction ID', data.processorTransactionId],
    ['Internal Transaction ID', data.internalTransactionId],
    ['Amount', `${data.amount} ${data.asset}`],
    ['Channel', capitalizeWords(data.channel?.replace(/_/g, ' '))],
    ['Country', data.country],
    ['Status', data.status],
    ['Date Initiated', formatDate.dateTimeInTimeZone(data.createdAt ?? '')],
    [
      'Date Finalized',
      data.finalizedAt && formatDate.dateTimeInTimeZone(data.finalizedAt),
    ],
    [
      'Rejection Reason',
      data.rejectionReason && <Json value={data.rejectionReason} />,
    ],
    [
      'Account Details',
      data.accountDetails && <Json value={data.accountDetails} />,
    ],
    [
      'Processor Response',
      data.processorResponseMeta && <Json value={data.processorResponseMeta} />,
    ],
  ];

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg w-full px-5 sm:px-10 py-5 sm:py-10">
      <div className="w-full flex justify-between items-center font-semibold">
        <h2>
          {capitalizeWords(data.type?.replace(/_/g, ' '))} Test Transaction
        </h2>
        <div className="text-cyan-700 uppercase">{data.processor}</div>
      </div>
      <hr className="border-1 border-gray-200 mt-2 mb-5 w-full" />

      {fieldsToDisplay.map(([label, value]) =>
        value ? (
          <div
            key={label}
            className="mb-2 py-2 px-3 flex gap-3 bg-gray-100 dark:bg-dark-inputBg justify-between"
          >
            <p className="text-slate-500 text-sm">{label}</p>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-200 w-2/3 overflow-hidden whitespace-normal">
              {value}
            </div>
          </div>
        ) : null,
      )}
    </div>
  );
}
